/**
 * Backup files — the only way a plan leaves this browser.
 *
 * With no server, a plan exists in exactly one place until someone downloads a
 * copy. The file is plain JSON so it survives email, messengers and cloud
 * drives, and opens anywhere: plan records exactly as stored, plus the photos
 * they reference, base64-encoded.
 *
 * Restoring is careful in one direction — never write anything from a file that
 * does not fully validate — and forgiving in the other: a photo already in this
 * browser is not written twice. Only its bytes say it is the same photo, though:
 * a file pairs ids and bytes however it likes, so an id this browser already
 * holds with other bytes keeps them, and the incoming photo gets an id of its
 * own. What a file never decides is bookkeeping — a plan's version and a
 * photo's age are this browser's.
 */

import { LIMITS } from '../config'
import { newId } from '../ids'
import { ApiError, type PlanSummary } from '../types'
import { type StoredAsset, type StoredPlan, asStorageError, getDb, nowIso } from './db'
import { summaryOf } from './plans'
import { readAssets } from './photos'
import { announce } from './tabs'
import { parseStoredPlan } from './validate'

export const BACKUP_FORMAT = 'yerbeyer-backup'
export const BACKUP_VERSION = 1

interface BackupPhoto {
  id: string
  type: string
  width: number
  height: number
  createdAt: string
  /** The image bytes, base64. */
  data: string
}

interface BackupFile {
  format: typeof BACKUP_FORMAT
  version: number
  exportedAt: string
  plans: StoredPlan[]
  photos: BackupPhoto[]
}

/** A photo read from a file: no age yet — that starts when it is stored here. */
export type BackupAsset = Omit<StoredAsset, 'createdAt'>

/** A backup file that has been read and fully validated, ready to restore. */
export interface Backup {
  exportedAt: string
  plans: StoredPlan[]
  photos: BackupAsset[]
}

export type ConflictChoice = 'replace' | 'copy' | 'skip'

export interface RestoreOptions {
  /** Asked once per plan that already exists in this browser, in file order. */
  decide: (incoming: PlanSummary, existing: PlanSummary) => Promise<ConflictChoice>
  /** The title a kept-both copy gets. */
  copyTitle: (title: string) => string
}

export interface RestoreResult {
  restored: number
  skipped: number
}

/** Kept per plan in `meta`, so the dashboard can tell what has changed since. */
export interface BackupMark {
  at: string
  version: number
}

const MARK_PREFIX = 'backup:'
/** Photos are stored at 512 px; anything this large in a backup is not ours. */
const MAX_PHOTO_BYTES = 4 * 1024 * 1024
const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

const invalid = (why: string) => new ApiError(422, 'backup_invalid', why)

const photoIdsOf = (plan: StoredPlan): string[] =>
  plan.guests.flatMap((guest) => (guest.photoAssetId ? [guest.photoAssetId] : []))

// --- writing ----------------------------------------------------------------

export async function createBackup(
  planIds: string[],
): Promise<{ blob: Blob; plans: StoredPlan[] }> {
  try {
    const db = await getDb()
    const tx = db.transaction('plans', 'readonly')
    const found = await Promise.all(planIds.map((id) => tx.store.get(id)))
    await tx.done
    const plans = found.filter((plan): plan is StoredPlan => Boolean(plan))
    if (plans.length === 0) throw new ApiError(404, 'not_found', 'Nothing to back up.')

    const assets = await readAssets(plans.flatMap(photoIdsOf))
    const photos: BackupPhoto[] = await Promise.all(
      assets.map(async (asset) => ({
        id: asset.id,
        type: asset.type,
        width: asset.width,
        height: asset.height,
        createdAt: asset.createdAt,
        data: toBase64(new Uint8Array(await asset.blob.arrayBuffer())),
      })),
    )

    const file: BackupFile = {
      format: BACKUP_FORMAT,
      version: BACKUP_VERSION,
      exportedAt: nowIso(),
      plans,
      photos,
    }
    return { blob: new Blob([JSON.stringify(file)], { type: 'application/json' }), plans }
  } catch (error) {
    throw asStorageError(error)
  }
}

/** Remember that these plans, at these versions, are safely in a file. */
export async function markBackedUp(plans: Array<{ id: string; version: number }>): Promise<void> {
  const db = await getDb()
  const tx = db.transaction('meta', 'readwrite')
  const at = nowIso()
  await Promise.all(
    plans.map((plan) => {
      const mark: BackupMark = { at, version: plan.version }
      return tx.store.put(mark, MARK_PREFIX + plan.id)
    }),
  )
  await tx.done
}

/** Saves since the last backup after which the editor starts reminding. */
export const REMIND_AFTER_SAVES = 40
/** …or this long since the last backup, if anything has changed at all. */
export const REMIND_AFTER_MS = 3 * 24 * 60 * 60 * 1000

/**
 * Is a plan's backup due? Only if it changed since its last backup (a plan
 * never backed up counts from its creation), and then only once a session's
 * worth of saves or a few days have gone by — a reminder after every edit would
 * be noise, and noise gets ignored exactly when it matters.
 */
export function backupDue(
  plan: { version: number; createdAt: string },
  mark: BackupMark | undefined,
  now: number = Date.now(),
): boolean {
  const since = mark ?? { version: 1, at: plan.createdAt }
  if (plan.version <= since.version) return false
  if (plan.version - since.version >= REMIND_AFTER_SAVES) return true
  const at = Date.parse(since.at)
  return Number.isFinite(at) && now - at >= REMIND_AFTER_MS
}

export async function backupMarks(): Promise<Map<string, BackupMark>> {
  const db = await getDb()
  const range = IDBKeyRange.bound(MARK_PREFIX, MARK_PREFIX + String.fromCharCode(0xffff))
  const tx = db.transaction('meta', 'readonly')
  const [keys, values] = await Promise.all([tx.store.getAllKeys(range), tx.store.getAll(range)])
  await tx.done
  const marks = new Map<string, BackupMark>()
  keys.forEach((key, index) => {
    marks.set(String(key).slice(MARK_PREFIX.length), values[index] as BackupMark)
  })
  return marks
}

// --- reading ----------------------------------------------------------------

export async function readBackup(file: Blob): Promise<Backup> {
  let raw: unknown
  try {
    raw = JSON.parse(await file.text())
  } catch {
    throw invalid('not JSON')
  }
  if (typeof raw !== 'object' || raw === null) throw invalid('not an object')
  const data = raw as Partial<BackupFile>
  if (data.format !== BACKUP_FORMAT) throw invalid('not a Yerbəyer backup')
  if (typeof data.version !== 'number' || !Number.isInteger(data.version) || data.version < 1) {
    throw invalid('unknown format version')
  }
  if (data.version > BACKUP_VERSION) {
    throw new ApiError(422, 'backup_too_new', `format version ${data.version} is newer than this app`)
  }
  if (!Array.isArray(data.plans) || data.plans.length === 0) throw invalid('no plans')
  if (!Array.isArray(data.photos)) throw invalid('no photo list')

  const plans = data.plans.map((plan, index) => parseStoredPlan(plan, `plans[${index}]`))
  if (new Set(plans.map((plan) => plan.id)).size !== plans.length) throw invalid('duplicate plan ids')

  const photos = data.photos.map((photo, index) => parsePhoto(photo, `photos[${index}]`))
  if (new Set(photos.map((photo) => photo.id)).size !== photos.length) throw invalid('duplicate photo ids')
  return { exportedAt: typeof data.exportedAt === 'string' ? data.exportedAt : '', plans, photos }
}

function parsePhoto(raw: unknown, path: string): BackupAsset {
  if (typeof raw !== 'object' || raw === null) throw invalid(`${path}: not an object`)
  const photo = raw as Partial<BackupPhoto>
  if (typeof photo.id !== 'string' || !/^[0-9a-f-]{36}$/i.test(photo.id)) throw invalid(`${path}.id`)
  if (typeof photo.type !== 'string' || !PHOTO_TYPES.has(photo.type)) throw invalid(`${path}.type`)
  const size = (value: unknown) =>
    typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= 8192
  if (!size(photo.width) || !size(photo.height)) throw invalid(`${path}: size`)
  if (typeof photo.data !== 'string' || photo.data.length > (MAX_PHOTO_BYTES * 4) / 3 + 4) {
    throw invalid(`${path}.data`)
  }
  let bytes: Uint8Array<ArrayBuffer>
  try {
    bytes = fromBase64(photo.data)
  } catch {
    throw invalid(`${path}.data: not base64`)
  }
  return {
    id: photo.id.toLowerCase(),
    blob: new Blob([bytes], { type: photo.type }),
    type: photo.type,
    width: photo.width as number,
    height: photo.height as number,
  }
}

// --- restoring --------------------------------------------------------------

/**
 * Write a validated backup into this browser.
 *
 * Every question is asked before anything is written, then one transaction
 * writes plans and photos together — so cancelling halfway, or a full disk,
 * leaves the browser exactly as it was.
 */
export async function restoreBackup(
  backup: Backup,
  options: RestoreOptions,
): Promise<RestoreResult> {
  const db = await getDb()

  const lookup = db.transaction('plans', 'readonly')
  const [existing, total] = await Promise.all([
    Promise.all(backup.plans.map((plan) => lookup.store.get(plan.id))),
    lookup.store.count(),
  ])
  await lookup.done

  const writes: StoredPlan[] = []
  const replaced: StoredPlan[] = []
  let skipped = 0
  let added = 0

  // A version is this browser's count of saves, the base of every conflict
  // check — never the file's. A file's 2^53 would stop counting, and stale
  // saves from another tab would no longer be refused.
  for (const [index, incoming] of backup.plans.entries()) {
    const mine = existing[index]
    if (!mine) {
      writes.push({ ...incoming, version: 1 })
      added += 1
      continue
    }
    const choice = await options.decide(summaryOf(incoming), summaryOf(mine))
    if (choice === 'skip') {
      skipped += 1
    } else if (choice === 'replace') {
      // Past the version this browser holds, so an editor open on it in another
      // tab sees its next save refused and merges the restored plan in.
      const record = { ...incoming, version: mine.version + 1 }
      writes.push(record)
      replaced.push(record)
    } else {
      const now = nowIso()
      writes.push({
        ...incoming,
        id: newId(),
        title: options.copyTitle(incoming.title).slice(0, 160),
        version: 1,
        createdAt: now,
        updatedAt: now,
      })
      added += 1
    }
  }

  if (total + added > LIMITS.maxPlans) {
    const why = `A browser can hold at most ${LIMITS.maxPlans} plans.`
    throw new ApiError(403, 'quota_exceeded', why)
  }

  const needed = new Set(writes.flatMap(photoIdsOf))
  const incoming = backup.photos.filter((photo) => needed.has(photo.id))

  // Compared before the write: awaiting a Blob's bytes inside a transaction
  // would let it commit early.
  const stored = new Map((await readAssets(incoming.map((photo) => photo.id))).map((a) => [a.id, a]))
  const renamed = new Map<string, string>()
  const photos: BackupAsset[] = []
  for (const photo of incoming) {
    const mine = stored.get(photo.id)
    if (!mine) {
      photos.push(photo)
    } else if (!(await sameBytes(mine.blob, photo.blob))) {
      const id = newId()
      renamed.set(photo.id, id)
      photos.push({ ...photo, id })
    }
  }
  const records = renamed.size === 0 ? writes : writes.map((plan) => withPhotoIds(plan, renamed))

  try {
    const tx = db.transaction(['plans', 'assets', 'meta'], 'readwrite')
    const assets = tx.objectStore('assets')
    const present = await Promise.all(photos.map((photo) => assets.getKey(photo.id)))
    // A restored plan is, by definition, already in a file — say so, or the
    // dashboard would greet a restore by asking for a backup of what it just read.
    const at = nowIso()
    await Promise.all([
      // Aged from now, whatever the file says: garbage collection counts from
      // here, and a date in the future would keep a photo forever.
      ...photos
        .filter((_, index) => present[index] === undefined)
        .map((photo) => assets.put({ ...photo, createdAt: at })),
      ...records.map((plan) => tx.objectStore('plans').put(plan)),
      ...records.map((plan) => {
        const mark: BackupMark = { at, version: plan.version }
        return tx.objectStore('meta').put(mark, MARK_PREFIX + plan.id)
      }),
    ])
    await tx.done
  } catch (error) {
    throw asStorageError(error)
  }

  for (const plan of replaced) announce({ type: 'saved', planId: plan.id, version: plan.version })
  return { restored: writes.length, skipped }
}

async function sameBytes(a: Blob, b: Blob): Promise<boolean> {
  if (a.size !== b.size) return false
  const [x, y] = await Promise.all([a.arrayBuffer(), b.arrayBuffer()])
  const left = new Uint8Array(x)
  const right = new Uint8Array(y)
  return left.every((byte, index) => byte === right[index])
}

function withPhotoIds(plan: StoredPlan, renamed: Map<string, string>): StoredPlan {
  return {
    ...plan,
    guests: plan.guests.map((guest) => {
      const id = guest.photoAssetId ? renamed.get(guest.photoAssetId) : undefined
      return id ? { ...guest, photoAssetId: id } : guest
    }),
  }
}

// --- base64 -----------------------------------------------------------------

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function fromBase64(data: string): Uint8Array<ArrayBuffer> {
  const binary = atob(data)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}
